import { injectable } from 'inversify';
import { BranchInventory, StockMovement, StockMovementType } from '../../domain/models/BranchInventory';
import type { BranchInventoryRepository } from '../../domain/repositories/BranchInventoryRepository';
import { databaseConfig } from '../../config/database';

/**
 * Implementation of BranchInventoryRepository using DatabaseDataSource
 */
@injectable()
export class BranchInventoryRepositoryImpl implements BranchInventoryRepository {
  private readonly baseUrl: string;

  constructor() {
    this.baseUrl = databaseConfig.mongodb.apiBaseUrl;
  }

  async getByBranchAndProduct(branchId: string, productId: string): Promise<BranchInventory | null> {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/inventory/branch/${branchId}/product/${productId}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`Failed to fetch inventory: ${response.statusText}`);
      }

      const result = await response.json();
      const record = result.data || result;
      return this.mapToBranchInventory(record);
    } catch (error) {
      console.error('❌ Failed to fetch inventory by branch and product:', error);
      return null;
    }
  }

  async getByBranch(branchId: string): Promise<BranchInventory[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/inventory/branch/${branchId}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch branch inventory: ${response.statusText}`);
      }

      const result = await response.json();
      const data = result.data || [];
      
      console.log(`📦 Loaded ${data.length} inventory items for branch ${branchId}`);
      return data.map((record: any) => this.mapToBranchInventory(record));
    } catch (error) {
      console.error('❌ Failed to fetch branch inventory:', error);
      return [];
    }
  }

  async getByProduct(productId: string): Promise<BranchInventory[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/inventory/product/${productId}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch product inventory: ${response.statusText}`);
      }

      const result = await response.json();
      const data = result.data || [];
      return data.map((record: any) => this.mapToBranchInventory(record));
    } catch (error) {
      console.error('❌ Failed to fetch product inventory:', error);
      return [];
    }
  }

  // Basic implementations for interface compliance - will be expanded later
  async updateStock(_branchId: string, _productId: string, _newStock: number, _userId: string, _reason: string): Promise<BranchInventory> {
    throw new Error('updateStock not implemented yet');
  }

  async adjustStock(_branchId: string, _productId: string, _adjustment: number, _userId: string, _reason: string): Promise<BranchInventory> {
    throw new Error('adjustStock not implemented yet');
  }

  async updateExposedOnSite(_branchId: string, _productId: string, _newExposed: number, _userId: string, _reason: string): Promise<BranchInventory> {
    throw new Error('updateExposedOnSite not implemented yet');
  }

  async adjustExposedOnSite(_branchId: string, _productId: string, _adjustment: number, _userId: string, _reason: string): Promise<BranchInventory> {
    throw new Error('adjustExposedOnSite not implemented yet');
  }

  async restockFromStorage(_branchId: string, _productId: string, _quantity: number, _userId: string): Promise<BranchInventory> {
    throw new Error('restockFromStorage not implemented yet');
  }

  async moveToStorage(_branchId: string, _productId: string, _quantity: number, _userId: string): Promise<BranchInventory> {
    throw new Error('moveToStorage not implemented yet');
  }

  async reserveStock(_branchId: string, _productId: string, _quantity: number): Promise<boolean> {
    throw new Error('reserveStock not implemented yet');
  }

  async releaseStock(_branchId: string, _productId: string, _quantity: number): Promise<boolean> {
    throw new Error('releaseStock not implemented yet');
  }

  async recordStockMovement(_movement: Omit<StockMovement, 'id' | 'createdAt'>): Promise<StockMovement> {
    throw new Error('recordStockMovement not implemented yet');
  }

  async getStockMovements(_branchId: string, _productId?: string, _limit?: number): Promise<StockMovement[]> {
    throw new Error('getStockMovements not implemented yet');
  }

  async getStockMovementsByType(_branchId: string, _type: StockMovementType, _limit?: number): Promise<StockMovement[]> {
    throw new Error('getStockMovementsByType not implemented yet');
  }

  async getLowStockItems(_branchId: string): Promise<BranchInventory[]> {
    throw new Error('getLowStockItems not implemented yet');
  }

  async getOutOfStockItems(branchId: string): Promise<BranchInventory[]> {
    try {
      const allInventory = await this.getByBranch(branchId);
      return allInventory.filter(inv => inv.exposedOnSite <= 0);
    } catch (error) {
      console.error('❌ Failed to get out of stock items:', error);
      return [];
    }
  }

  async getItemsNeedingRestock(_branchId: string): Promise<BranchInventory[]> {
    throw new Error('getItemsNeedingRestock not implemented yet');
  }

  async bulkUpdateStock(_updates: Array<{
    branchId: string;
    productId: string;
    newStock: number;
    userId: string;
    reason: string;
  }>): Promise<BranchInventory[]> {
    throw new Error('bulkUpdateStock not implemented yet');
  }

  async initializeBranchInventory(_branchId: string, _productId: string, _initialStock?: number, _initialExposed?: number): Promise<BranchInventory> {
    throw new Error('initializeBranchInventory not implemented yet');
  }

  async initializeProductForAllBranches(_productId: string, _initialStock?: number, _initialExposed?: number): Promise<BranchInventory[]> {
    throw new Error('initializeProductForAllBranches not implemented yet');
  }

  private mapToBranchInventory(record: any): BranchInventory {
    return {
      id: record.visibleId || record.id,
      branchId: record.branchId,
      productId: record.productId,
      productSku: record.productSku || '',
      stock: record.stock ?? 0,
      exposedOnSite: record.exposedOnSite ?? 0,
      reservedStock: record.reservedStock ?? 0,
      minimumExposed: record.minimumExposed ?? 0,
      maximumExposed: record.maximumExposed ?? 0,
      lastStockUpdate: record.lastStockUpdate ? new Date(record.lastStockUpdate) : new Date(),
      createdAt: record.createdAt ? new Date(record.createdAt) : new Date(),
      updatedAt: record.updatedAt ? new Date(record.updatedAt) : new Date()
    };
  }
}
